import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { CreditCard, CalendarClock, ShieldCheck, Tag, CalendarCheck, Lock, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
  } catch {
    return "—";
  }
};

export function ManageMembership() {
  const [m, setM] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(() => {
    api.get("/portal/membership").then((r) => setM(r.data)).catch(() => setM({ active: false }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const cancel = async () => {
    setBusy(true);
    try {
      await api.post("/portal/membership/cancel");
      toast.success("Your membership will end at the close of the current billing period.");
      setConfirming(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel right now");
    } finally {
      setBusy(false);
    }
  };

  if (!m) return null;

  // Not a member — invitation card.
  if (!m.active && m.status !== "active") {
    return (
      <div data-testid="manage-membership" className="bg-ivory border border-espresso/10 rounded-2xl p-8">
        <div className="overline mb-2">Membership</div>
        <h3 className="font-serif text-2xl text-espresso mb-3">You're not a member yet</h3>
        <p className="text-taupe text-sm mb-6 max-w-lg">Join a monthly wellness plan to save on every visit, unlock 20% off supplements and get priority booking.</p>
        <Link to="/memberships" data-testid="membership-join-cta" className="inline-block bg-espresso text-ivory rounded-full px-7 py-3 text-xs uppercase tracking-[0.18em] hover:bg-brass transition-colors">View Plans</Link>
      </div>
    );
  }

  const perks = m.perks || {};

  return (
    <div data-testid="manage-membership" className="grid lg:grid-cols-2 gap-6">
      {/* Plan card */}
      <div className="bg-espresso text-ivory rounded-2xl p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="grid place-items-center h-11 w-11 rounded-xl bg-brass/20"><CreditCard size={20} className="text-brass" /></div>
          <div>
            <div className="overline !text-brass">Your Membership</div>
            <h3 className="font-serif text-2xl" data-testid="membership-plan-name">{m.plan_name}</h3>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between border-b border-ivory/10 pb-4">
            <span className="inline-flex items-center gap-2 text-ivory/70 text-sm"><ShieldCheck size={16} className="text-brass" /> Status</span>
            <span className="text-sm capitalize" data-testid="membership-status">
              {m.cancel_at_period_end ? "Ending soon" : (m.status || "active")}
            </span>
          </div>
          {m.current_period_end && (
            <div className="flex items-center justify-between border-b border-ivory/10 pb-4">
              <span className="inline-flex items-center gap-2 text-ivory/70 text-sm"><CalendarClock size={16} className="text-brass" /> {m.cancel_at_period_end ? "Ends on" : "Next billing"}</span>
              <span className="text-sm" data-testid="membership-next-billing">{fmtDate(m.current_period_end)}</span>
            </div>
          )}
          {m.locked && (
            <div className="flex items-start gap-2 bg-brass/10 border border-brass/25 rounded-lg px-4 py-3 text-xs text-ivory/85" data-testid="membership-lockin">
              <Lock size={14} className="text-brass shrink-0 mt-0.5" />
              New memberships include a 2-month minimum. You can cancel after <span className="text-ivory">{fmtDate(m.lock_in_until)}</span>.
            </div>
          )}
        </div>

        <div className="mt-7">
          {m.cancel_at_period_end ? (
            <p className="text-ivory/60 text-sm">Your membership is set to end on {fmtDate(m.current_period_end)}. You'll keep all benefits until then.</p>
          ) : confirming ? (
            <div className="flex flex-col sm:flex-row gap-3" data-testid="membership-cancel-confirm">
              <button onClick={cancel} disabled={busy} className="flex-1 rounded-full py-3 text-xs uppercase tracking-[0.16em] bg-ivory text-espresso hover:bg-brass hover:text-ivory transition-colors disabled:opacity-60" data-testid="membership-cancel-yes">
                {busy ? "Cancelling…" : "Yes, cancel at period end"}
              </button>
              <button onClick={() => setConfirming(false)} disabled={busy} className="flex-1 rounded-full py-3 text-xs uppercase tracking-[0.16em] border border-ivory/30 hover:bg-ivory/10 transition-colors">Keep my plan</button>
            </div>
          ) : (
            <button
              onClick={() => setConfirming(true)}
              disabled={!m.can_cancel}
              data-testid="membership-cancel-btn"
              className={`text-xs uppercase tracking-[0.16em] rounded-full px-6 py-3 transition-colors ${m.can_cancel ? "border border-ivory/30 text-ivory/80 hover:bg-ivory/10" : "border border-ivory/15 text-ivory/35 cursor-not-allowed"}`}
            >
              {m.locked ? "Locked for 2 months" : "Cancel membership"}
            </button>
          )}
        </div>
      </div>

      {/* Perks card */}
      <div className="bg-gradient-to-br from-brass via-[#d8a24a] to-[#b9812f] text-ivory rounded-2xl p-8" data-testid="member-perks">
        <div className="flex items-center gap-3 mb-6">
          <Sparkles size={22} className="text-ivory" />
          <h3 className="font-serif text-2xl">Member perks</h3>
        </div>
        <div className="space-y-5">
          <div className="flex items-start gap-4">
            <div className="grid place-items-center h-11 w-11 rounded-xl bg-ivory/20 shrink-0"><Tag size={18} /></div>
            <div>
              <div className="font-serif text-lg">{perks.discount_pct || 20}% off supplements & elderberry</div>
              <div className="text-ivory/80 text-sm">Applied automatically in-clinic — or use code <span className="font-mono bg-ivory/20 rounded px-2 py-0.5" data-testid="member-discount-code">{perks.discount_code || "MEMBER20"}</span></div>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="grid place-items-center h-11 w-11 rounded-xl bg-ivory/20 shrink-0"><CalendarCheck size={18} /></div>
            <div className="flex-1">
              <div className="font-serif text-lg">Priority booking</div>
              <div className="text-ivory/80 text-sm mb-3">Members get first pick of appointment times.</div>
              {perks.booking_url && (
                <a href={perks.booking_url} target="_blank" rel="noreferrer" data-testid="member-priority-booking" className="inline-block bg-ivory text-espresso rounded-full px-6 py-2.5 text-xs uppercase tracking-[0.16em] hover:bg-espresso hover:text-ivory transition-colors">
                  Book Priority Visit
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
